const asArray = value => (Array.isArray(value) ? value : []);

/** Coerce DB/API preset_questions into a string[] for forms. */
export const normalizePresetQuestions = value => {
  if (Array.isArray(value)) {
    return value.map(q => (q == null ? '' : String(q)));
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map(q => (q == null ? '' : String(q)));
      }
    } catch {
      /* plain string */
    }
    return [trimmed];
  }
  if (value && typeof value === 'object') {
    if (Array.isArray(value.questions)) {
      return normalizePresetQuestions(value.questions);
    }
    return Object.values(value)
      .filter(v => typeof v === 'string' || typeof v === 'number')
      .map(String);
  }
  return [];
};

export const normalizeAgent = agent => {
  if (!agent || typeof agent !== 'object') return agent;
  const next = { ...agent };
  const id = agent.id || agent._id || '';
  if (id) {
    next.id = id;
    next._id = agent._id || agent.id || id;
  }
  if ('isRootAgent' in agent || 'is_root_agent' in agent) {
    next.isRootAgent = agent.isRootAgent === true || agent.is_root_agent === true;
  }
  if ('isActive' in agent || 'is_active' in agent) {
    next.isActive =
      agent.isActive !== undefined ? agent.isActive : agent.is_active !== false;
  }
  if ('presetQuestions' in agent || 'preset_questions' in agent) {
    next.presetQuestions = normalizePresetQuestions(
      agent.presetQuestions !== undefined ? agent.presetQuestions : agent.preset_questions
    );
  } else {
    next.presetQuestions = [];
  }
  if ('havePresetQuestions' in agent || 'have_preset_questions' in agent) {
    next.havePresetQuestions =
      agent.havePresetQuestions === true || agent.have_preset_questions === true;
  }
  return next;
};

export const normalizeUser = user => {
  if (!user || typeof user !== 'object') return user;
  const next = { ...user };
  const id = user.id || user._id || '';
  if (id) {
    next.id = id;
    next._id = user._id || user.id || id;
  }
  // Always normalize to a boolean so UI gates (AgentForm, Settings, Testing) stay consistent
  next.useRootCredentials =
    user.useRootCredentials === true || user.use_root_credentials === true;
  if ('rootAgentIds' in user || 'root_agent_ids' in user) {
    next.rootAgentIds = asArray(user.rootAgentIds).length
      ? asArray(user.rootAgentIds)
      : asArray(user.root_agent_ids);
  }
  return next;
};

export const normalizePlan = plan => {
  if (!plan || typeof plan !== 'object') return plan;
  return {
    ...plan,
    id: plan.id || plan._id || '',
    _id: plan._id || plan.id || '',
    useRootCredentials:
      plan.useRootCredentials === true || plan.use_root_credentials === true,
    rootAgentIds: asArray(plan.rootAgentIds).length
      ? asArray(plan.rootAgentIds)
      : asArray(plan.root_agent_ids),
  };
};

export const normalizeAgentsResponse = response => {
  if (!response || typeof response !== 'object') return response;
  const next = { ...response };
  if (Array.isArray(next.data?.agents)) {
    next.data = { ...next.data, agents: next.data.agents.map(normalizeAgent) };
  }
  if (next.data?.agent) {
    next.data = { ...next.data, agent: normalizeAgent(next.data.agent) };
  }
  if (Array.isArray(next.agents)) {
    next.agents = next.agents.map(normalizeAgent);
  }
  if (next.agent) {
    next.agent = normalizeAgent(next.agent);
  }
  if (Array.isArray(next.data)) {
    next.data = next.data.map(normalizeAgent);
  }
  return next;
};

export const normalizeUsersResponse = response => {
  if (!response || typeof response !== 'object') return response;
  const next = { ...response };
  if (Array.isArray(next.data?.users)) {
    next.data = { ...next.data, users: next.data.users.map(normalizeUser) };
  }
  if (next.data?.user) {
    next.data = { ...next.data, user: normalizeUser(next.data.user) };
  }
  if (Array.isArray(next.users)) {
    next.users = next.users.map(normalizeUser);
  }
  if (next.user) {
    next.user = normalizeUser(next.user);
  }
  if (Array.isArray(next.data)) {
    next.data = next.data.map(normalizeUser);
  }
  return next;
};

export const normalizePlansResponse = response => {
  if (!response || typeof response !== 'object') return response;
  const next = { ...response };
  if (Array.isArray(next.data?.plans)) {
    next.data = { ...next.data, plans: next.data.plans.map(normalizePlan) };
  }
  if (next.data?.plan) {
    next.data = { ...next.data, plan: normalizePlan(next.data.plan) };
  }
  if (Array.isArray(next.plans)) {
    next.plans = next.plans.map(normalizePlan);
  }
  if (next.plan) {
    next.plan = normalizePlan(next.plan);
  }
  if (Array.isArray(next.data)) {
    next.data = next.data.map(normalizePlan);
  }
  return next;
};
